import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  ujmsReportsOverview, ujmsRunReport, ujmsSavedReports, ujmsSaveReport, ujmsDeleteSavedReport,
  ujmsDocketEventTypes, ujmsEventListing,
} from "../../services/ujmsApi";
import api from "../../services/api";
import { Link } from "react-router-dom";
import { Panel, thCls, tdCls, inputCls, selectCls, btnPrimary, btnGhost, Field, cls } from "../ui";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { BarChart3, Trash2, Download } from "lucide-react";

const err = (e, fallback) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : fallback);

const GROUPS = {
  cases: ["case_type", "status", "case_subtype", "month"],
  hearings: ["hearing_type", "status", "courtroom", "judicial_officer", "month"],
  filings: ["filing_type", "status", "month"],
  transactions: ["type", "category", "month"],
};

export default function Reports() {
  const [tab, setTab] = useState("Dashboards");
  return (
    <div className="space-y-4" data-testid="ujms-reports">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2"><BarChart3 className="w-6 h-6" /> Reporting & Analytics</h1>
        <p className="text-sm text-slate-500">Caseload, clearance and finance trends, plus a self-service report builder over whitelisted court data.</p>
      </div>
      <div className="border-b border-slate-200 flex gap-1">
        {["Dashboards", "Report Builder", "Event Listing"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`reports-tab-${t.split(" ")[0].toLowerCase()}`}
            className={cls("px-3.5 py-2 text-sm border-b-2", tab === t ? "border-blue-900 text-blue-900 font-semibold" : "border-transparent text-slate-500 hover:text-slate-800")}>
            {t}
          </button>
        ))}
      </div>
      {tab === "Dashboards" ? <Dashboards /> : tab === "Report Builder" ? <ReportBuilder /> : <EventListing />}
    </div>
  );
}

function Dashboards() {
  const [d, setD] = useState(null);
  useEffect(() => { ujmsReportsOverview().then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="text-sm text-slate-400 p-4">Computing analytics…</div>;
  const money = (n) => `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="report-cards">
        {[["Open Cases", d.cards.open_cases], ["Disposed", d.cards.disposed_cases],
          ["Avg Age (days)", d.cards.avg_open_age_days], ["Hearings Set", d.cards.hearings_scheduled],
          ["Assessed", money(d.cards.total_assessed)], ["Collected", money(d.cards.total_collected)],
          ["Collection Rate", `${d.cards.collection_rate}%`]].map(([label, v]) => (
          <div key={label} className="bg-white border border-slate-200 rounded-md px-4 py-3">
            <div className="text-xl font-semibold text-slate-900">{v}</div>
            <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Panel title="Filings vs dispositions (clearance) — last 12 months" testId="clearance-chart">
          <div className="p-3 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={d.clearance}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip /><Legend />
                <Line type="monotone" dataKey="filed" stroke="#1e3a8a" strokeWidth={2} dot={false} name="Filed" />
                <Line type="monotone" dataKey="disposed" stroke="#b45309" strokeWidth={2} dot={false} name="Disposed" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Court finance — assessed vs collected" testId="finance-chart">
          <div className="p-3 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d.finance_by_month}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip /><Legend />
                <Area type="monotone" dataKey="assessed" stroke="#1e3a8a" fill="#1e3a8a22" name="Assessed ($)" />
                <Area type="monotone" dataKey="collected" stroke="#047857" fill="#04785722" name="Collected ($)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Open caseload by case type" testId="caseload-chart">
          <div className="p-3 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.open_by_type}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="case_type" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#1e3a8a" name="Open cases" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Pending case age distribution" testId="age-chart">
          <div className="p-3 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.age_buckets}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#b45309" name="Open cases" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>
    </div>
  );
}

function ReportBuilder() {
  const [f, setF] = useState({ entity: "cases", group_by: "case_type", metric: "count", date_from: "", date_to: "" });
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState([]);
  const [name, setName] = useState("");
  const loadSaved = useCallback(() => { ujmsSavedReports().then((r) => setSaved(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  const run = async (def = f) => {
    try {
      const payload = { ...def, date_from: def.date_from || null, date_to: def.date_to || null, filters: {} };
      const r = await ujmsRunReport(payload);
      setResult(r.data);
    } catch (e) { err(e, "Report failed"); }
  };
  const save = async () => {
    if (!name.trim()) { toast.error("Give the report a name"); return; }
    try { await ujmsSaveReport({ name, definition: f }); toast.success("Report saved"); setName(""); loadSaved(); }
    catch (e) { err(e, "Failed to save"); }
  };
  const del = async (r) => {
    try { await ujmsDeleteSavedReport(r.id); toast.success("Saved report deleted"); loadSaved(); }
    catch (e) { err(e, "Failed"); }
  };
  const csv = () => {
    if (!result) return;
    const header = ["group", "count", ...(result.rows.some((r) => "sum" in r) ? ["sum"] : [])];
    const lines = [header.join(",")].concat(result.rows.map((r) => header.map((h) => JSON.stringify(r[h] ?? "")).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `ujms-report-${result.entity}-${result.group_by}.csv`;
    a.click();
  };

  return (
    <div className="space-y-4">
      <Panel title="Build a report — whitelisted entities, groupings and date ranges" testId="report-builder-panel">
        <div className="p-3 flex flex-wrap items-end gap-2">
          <Field label="Entity">
            <select className={`${selectCls} w-40`} value={f.entity}
              onChange={(e) => setF({ ...f, entity: e.target.value, group_by: GROUPS[e.target.value][0], metric: "count" })} data-testid="rb-entity">
              {Object.keys(GROUPS).map((k) => <option key={k}>{k}</option>)}
            </select>
          </Field>
          <Field label="Group by">
            <select className={`${selectCls} w-44`} value={f.group_by} onChange={(e) => setF({ ...f, group_by: e.target.value })} data-testid="rb-groupby">
              {GROUPS[f.entity].map((g) => <option key={g}>{g}</option>)}
            </select>
          </Field>
          <Field label="Metric">
            <select className={`${selectCls} w-36`} value={f.metric} onChange={(e) => setF({ ...f, metric: e.target.value })} data-testid="rb-metric">
              <option value="count">count</option>
              {f.entity === "transactions" && <option value="sum_amount">sum_amount</option>}
            </select>
          </Field>
          <Field label="From"><input type="date" className={`${inputCls} w-38`} value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="rb-from" /></Field>
          <Field label="To"><input type="date" className={`${inputCls} w-38`} value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="rb-to" /></Field>
          <button className={btnPrimary} onClick={() => run()} data-testid="rb-run-btn">Run Report</button>
          <div className="flex gap-1 items-end">
            <Field label="Save as"><input className={`${inputCls} w-44`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Monthly clearance…" data-testid="rb-save-name" /></Field>
            <button className={btnGhost} onClick={save} data-testid="rb-save-btn">Save</button>
          </div>
        </div>
      </Panel>

      {result && (
        <Panel title={`Results — ${result.entity} by ${result.group_by} (${result.total_rows} groups, ${result.total_count} records)`} testId="rb-results-panel">
          <div className="p-3">
            <div className="h-64 mb-3">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={result.rows}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="group" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey={result.metric === "sum_amount" ? "sum" : "count"} fill="#1e3a8a" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <button className={btnGhost} onClick={csv} data-testid="rb-csv-btn"><Download className="w-3.5 h-3.5" /> Download CSV</button>
          </div>
          <table className="w-full border-collapse">
            <thead><tr><th className={thCls}>Group</th><th className={thCls}>Count</th>{result.rows.some((r) => "sum" in r) && <th className={thCls}>Sum ($)</th>}</tr></thead>
            <tbody>
              {result.rows.map((r) => (
                <tr key={r.group} className="hover:bg-slate-50" data-testid={`rb-row-${r.group}`}>
                  <td className={tdCls}>{r.group}</td>
                  <td className={`${tdCls} font-mono`}>{r.count}</td>
                  {"sum" in r && <td className={`${tdCls} font-mono`}>{r.sum?.toLocaleString()}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      <Panel title="Saved reports" testId="saved-reports-panel">
        <table className="w-full border-collapse">
          <thead><tr><th className={thCls}>Name</th><th className={thCls}>Definition</th><th className={thCls}>Created By</th><th className={thCls}></th></tr></thead>
          <tbody>
            {saved.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`saved-report-${r.name.replace(/\s+/g, "-").toLowerCase()}`}>
                <td className={`${tdCls} font-medium`}>{r.name}</td>
                <td className={`${tdCls} font-mono text-xs`}>{r.definition.entity} by {r.definition.group_by} ({r.definition.metric})</td>
                <td className={`${tdCls} text-xs`}>{r.created_by}</td>
                <td className={`${tdCls} whitespace-nowrap`}>
                  <div className="flex gap-1.5">
                    <button className={btnGhost} onClick={() => { setF({ ...f, ...r.definition }); run(r.definition); }} data-testid={`saved-run-${r.name.replace(/\s+/g, "-").toLowerCase()}`}>Run</button>
                    <button className="text-red-600 hover:text-red-800 px-1" onClick={() => del(r)} data-testid={`saved-delete-${r.name.replace(/\s+/g, "-").toLowerCase()}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!saved.length && <tr><td className={`${tdCls} text-slate-400`} colSpan={4}>No saved reports.</td></tr>}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}

function EventListing() {
  const [types, setTypes] = useState([]);
  const [eventCode, setEventCode] = useState("");
  const [from, setFrom] = useState(new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { ujmsDocketEventTypes().then((r) => setTypes(r.data.event_types)).catch(() => {}); }, []);
  const run = async () => {
    if (!eventCode) { toast.error("Pick an event type"); return; }
    setBusy(true);
    try { const r = await ujmsEventListing({ event_code: eventCode, date_from: from, date_to: to }); setResult(r.data); }
    catch (e) { err(e, "Report failed"); }
    setBusy(false);
  };
  const exportCsv = async () => {
    try {
      const r = await api.get("/ujms/reports/event-listing", {
        params: { event_code: eventCode, date_from: from, date_to: to, format: "csv" }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `event_listing_${eventCode}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { err(e, "Export failed"); }
  };
  return (
    <div className="space-y-4" data-testid="event-listing">
      <Panel title="event listing — find every case with a given docket event in a date range" testId="event-listing-criteria">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Event type" required>
            <select className={cls(selectCls, "w-72")} value={eventCode} onChange={(e) => setEventCode(e.target.value)} data-testid="el-event-type">
              <option value="">Select an event…</option>
              {types.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="From"><input type="date" className={inputCls} value={from} onChange={(e) => setFrom(e.target.value)} data-testid="el-from" /></Field>
          <Field label="To"><input type="date" className={inputCls} value={to} onChange={(e) => setTo(e.target.value)} data-testid="el-to" /></Field>
          <button className={btnPrimary} onClick={run} disabled={busy} data-testid="el-run-btn">{busy ? "Running…" : "Run Report"}</button>
          {result && result.rows.length > 0 && (
            <button className={btnGhost} onClick={exportCsv} data-testid="el-export-csv"><Download className="w-3.5 h-3.5" /> Export to Excel (CSV)</button>
          )}
        </div>
      </Panel>
      {result && (
        <Panel title={`${result.count} case event(s) — ${result.event_code}`} testId="event-listing-results">
          <table className="w-full border-collapse">
            <thead><tr><th className={thCls}>Date</th><th className={thCls}>Case Number</th><th className={thCls}>Caption</th>
              <th className={thCls}>Type</th><th className={thCls}>Status</th><th className={thCls}>Description</th><th className={thCls}>Entered By</th></tr></thead>
            <tbody>
              {result.rows.map((r, i) => (
                <tr key={i} className="hover:bg-slate-50" data-testid={`el-row-${i}`}>
                  <td className={cls(tdCls, "font-mono text-xs")}>{r.date}</td>
                  <td className={cls(tdCls, "font-mono text-xs")}>
                    <Link to={`/ujms/cases/${r.case_id}`} className="text-ujms-dark hover:underline">{r.case_number}</Link>
                  </td>
                  <td className={tdCls}><span className="line-clamp-1 max-w-[240px]">{r.caption}</span></td>
                  <td className={cls(tdCls, "text-xs")}>{r.case_type}</td>
                  <td className={cls(tdCls, "text-xs")}>{r.case_status}</td>
                  <td className={cls(tdCls, "text-xs")}><span className="line-clamp-2 max-w-[320px]">{r.description}</span></td>
                  <td className={cls(tdCls, "text-xs")}>{r.entered_by}</td>
                </tr>
              ))}
              {!result.rows.length && <tr><td colSpan={7} className={tdCls}>No matching events in this window.</td></tr>}
            </tbody>
          </table>
        </Panel>
      )}
    </div>
  );
}
